import { TextField } from '@material-ui/core';
import CSS from 'csstype';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import React, { JSX, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { IDaypartingPeriod } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/common';
import { DaypartingDayToggles } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/DaypartingDayToggles';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  borderRadius,
  Col,
  Cols,
  Div,
  fontSizing,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
  TKey,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

interface IDaypartingPeriodFormProps {
  index: number;
  handleRemovePeriod: (idx: number) => void;
  isRemoveDisabled: boolean;
  key: string | number;
  period: IDaypartingPeriod;
  isHourOnly?: boolean;
}

const DaypartingPeriodForm = ({
  handleRemovePeriod,
  index,
  isRemoveDisabled,
  period,
  isHourOnly = false,
}: IDaypartingPeriodFormProps) => {
  const { start, end, days } = period;
  const { t } = usePublicaTranslation();

  const [startTime, setStartTime] = useState(start);
  const [endTime, setEndTime] = useState(end);
  const [activeDays, setActiveDays] = useState<Set<string>>(days || new Set([]));
  const [timeError, setTimeError] = useState('' as TKey);

  const timeFormat = isHourOnly ? 'HH:00' : 'HH:mm';

  const handleTimeChange = (time: string, point: 'start' | 'end') => {
    const newMoment = moment(time, timeFormat);
    if (point === 'start') {
      if (endTime.isBefore(newMoment)) {
        setTimeError('ErrorStartTimeMustBeBeforeEndTime');
      } else {
        unstable_batchedUpdates(() => {
          setStartTime(newMoment);
          setTimeError('');
        });
        period.start = newMoment;
      }
    } else {
      if (startTime.isAfter(newMoment)) {
        setTimeError('ErrorEndTimeMustBeAfterStartTime');
      } else {
        unstable_batchedUpdates(() => {
          setEndTime(newMoment);
          setTimeError('');
        });
        period.end = newMoment;
      }
    }
  };

  const handleToggleDay = (value: string) => {
    const clonedActiveDays = cloneDeep(activeDays);
    if (activeDays.has(value)) {
      clonedActiveDays.delete(value);
    } else {
      clonedActiveDays.add(value);
    }
    setActiveDays(clonedActiveDays);
    period.days = clonedActiveDays;
  };

  return (
    <Rows
      gap={spacing.small}
      smallVerticalPadding
      horizontalPadding
      style={{
        border: `1px solid ${publicaColors.lightGray}`,
        borderRadius: borderRadius.base,
        backgroundColor: publicaColors.white,
      }}
    >
      <Row noPadding>
        <Cols gap={spacing.large}>
          {/* START */}
          <Col>
            <Rows>
              <Row noPadding>
                <TextField
                  id='time-picker-start'
                  type='time'
                  label={t('Start')}
                  value={startTime.format(timeFormat)}
                  onChange={(e) => handleTimeChange(e.target.value, 'start')}
                  InputProps={{
                    style: timeInputStyle,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ marginTop: 0 }}
                />
              </Row>
            </Rows>
          </Col>
          {/* END */}
          <Col>
            <Rows>
              <Row noPadding>
                <TextField
                  id='time-picker-end'
                  type='time'
                  label={t('End')}
                  value={endTime.format(timeFormat)}
                  onChange={(e) => handleTimeChange(e.target.value, 'end')}
                  InputProps={{
                    style: timeInputStyle,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ marginTop: 0 }}
                />
              </Row>
            </Rows>
          </Col>
          <Col flex style={{ textAlign: 'right' }}>
            <Div
              onClick={() => !isRemoveDisabled && handleRemovePeriod(index)}
              style={{ cursor: isRemoveDisabled ? 'default' : 'pointer' }}
            >
              <IconV2
                name='close'
                color={isRemoveDisabled ? publicaColors.lightGray : publicaColors.veryDarkGray}
              />
            </Div>
          </Col>
        </Cols>
      </Row>
      {timeError && (
        <Row noPadding>
          <Text extraSmall color={publicaColors.red} tKey={timeError} />
        </Row>
      )}
      <Row noPadding>
        <Text small tKey='RepeatingOn' />
      </Row>
      <Row noPadding>
        <DaypartingDayToggles selected={activeDays} onClick={handleToggleDay} />
      </Row>
    </Rows>
  );
};

const timeInputStyle: CSS.Properties = {
  fontSize: fontSizing.small,
  width: '108px',
};

interface IProps {
  initialPeriods: IDaypartingPeriod[];
  isHourOnly?: boolean;
}

export const DaypartingScheduleBuilder = ({
  initialPeriods,
  isHourOnly = false,
}: IProps): JSX.Element => {
  const { object } = useFormBuilderStore();
  const { t } = usePublicaTranslation();

  const isMounted = useRef(false);
  const [periods, setPeriods] = useState(initialPeriods);
  const [counter, setCounter] = useState(1);

  const timeFormat = isHourOnly ? 'HH:00' : 'HH:mm';

  useEffect(() => {
    if (isMounted.current) {
      setPeriods(initialPeriods);
    } else {
      isMounted.current = true;
    }
  }, [initialPeriods]);

  const handleAddPeriod = () => {
    unstable_batchedUpdates(() => {
      setPeriods((prevPeriods) => {
        const clonedPeriods = cloneDeep(prevPeriods);
        clonedPeriods.push({
          reactKey: counter,
          start: moment('00:00', timeFormat),
          end: moment('12:00', timeFormat),
          days: new Set([]),
        });
        object.DaypartingUI.Periods = clonedPeriods;
        return clonedPeriods;
      });
      setCounter(counter + 1);
    });
  };

  const handleRemovePeriod = (idx: number) => {
    setPeriods((prevPeriods) => {
      const clonedPeriods = cloneDeep(prevPeriods);
      clonedPeriods.splice(idx, 1);
      object.DaypartingUI.Periods = clonedPeriods;
      return clonedPeriods;
    });
  };

  return (
    <Rows gap={spacing.base}>
      {periods.map((period, i) => {
        const isRemoveDisabled = periods.length === 1 && i === 0;
        return (
          <Row noPadding key={period.reactKey}>
            <DaypartingPeriodForm
              period={period}
              key={period.reactKey}
              handleRemovePeriod={handleRemovePeriod}
              index={i}
              isRemoveDisabled={isRemoveDisabled}
              isHourOnly={isHourOnly}
            />
          </Row>
        );
      })}
      <Row noPadding>
        <TextButton size='small' onClick={handleAddPeriod} disabled={periods.length >= 20}>
          {t('PlusAddItem', { item: t('TimePeriod') })}
        </TextButton>
      </Row>
    </Rows>
  );
};
